import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { categoriesEditService } from '../services/categoriesEdit.service';
import type { CategoryFormValues, SettingsCategory } from '../types/categoriesEdit.types';

export function useCategoriesEdit(id: string | null) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userId = profile?.id || '';
  const companyId = profile?.company_id || '';

  const detailQuery = useQuery({
    queryKey: ['settings1', 'categories', 'detail', id],
    queryFn: () => (id ? categoriesEditService.getCategoryDetail(id) : null),
    enabled: !!id,
  });

  const allQuery = useQuery({
    queryKey: ['settings1', 'categories', 'list'],
    queryFn: categoriesEditService.getAllCategories,
  });

  const initialValues = useMemo(() => {
    if (!detailQuery.data || !allQuery.data) return undefined;
    
    const category = detailQuery.data;
    const all = allQuery.data;

    const subCategories = all
      .filter((item) => item.parentId === category.id)
      .map((child) => ({
        id: child.id,
        name: child.name,
        code: child.code,
        description: child.description,
        subSubCategories: all
          .filter((item) => item.parentId === child.id)
          .map((grand) => ({
            id: grand.id,
            name: grand.name,
            code: grand.code,
            description: grand.description,
          })),
      }));

    return {
      name: category.name,
      code: category.code,
      description: category.description,
      type: category.type,
      status: category.status,
      icon: category.icon || 'ri-folder-line',
      itemCount: category.itemCount,
      parentId: category.parentId,
      subCategories,
    } as CategoryFormValues;
  }, [detailQuery.data, allQuery.data]);

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CategoryFormValues }) => 
      categoriesEditService.updateCategory(id, values, userId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings1', 'categories'] });
    },
  });

  const parentOptions = useMemo(() => {
    return (allQuery.data || [])
      .filter((item) => item.id !== id && !item.parentId) // Only top level parents or at least not self
      .map((item) => ({
        id: item.id,
        name: item.name,
        code: item.code,
      }));
  }, [allQuery.data, id]);

  return {
    category: detailQuery.data,
    initialValues,
    parentOptions,
    isLoading: detailQuery.isLoading || allQuery.isLoading || updateMutation.isPending,
    updateCategory: async (values: CategoryFormValues) => {
      if (!id) {
        throw new Error('Category id is missing');
      }
      return updateMutation.mutateAsync({ id, values });
    },
  };
}
